import { call } from '@advanza/api'
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertDialogPortal,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@advanza/shadcn-ui/src/components/ui/alertDialog'
import { Label } from '@advanza/shadcn-ui/src/components/ui/label'
import { Switch } from '@advanza/shadcn-ui/src/components/ui/switch'
import { Button, Icon, LoadingDots } from '@advanza/ui'
import { useRef, useState } from 'react'

interface SyncLocalizedServiceSubscriptionsButtonProps {
    service: {
        service_id: number
        canonical_service_id: number | null
    }
}

const SyncLocalizedServiceSubscriptionsButton = ({
    service,
}: SyncLocalizedServiceSubscriptionsButtonProps) => {
    const requestTokenRef = useRef(0)
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isSyncSuccessful, setIsSyncSuccessful] = useState(false)
    const [response, setResponse] = useState<unknown>(null)
    const [createInactive, setCreateInactive] = useState(false)

    const resetDialogState = () => {
        requestTokenRef.current += 1
        setIsLoading(false)
        setIsSyncSuccessful(false)
        setResponse(null)
        setCreateInactive(false)
    }

    const onOpenChange = (open: boolean) => {
        setIsOpen(open)
        if (!open) {
            resetDialogState()
        }
    }

    const sync = () => {
        if (isLoading) {
            return
        }
        const currentRequestToken = requestTokenRef.current
        setIsLoading(true)
        setIsSyncSuccessful(false)
        call('office/subscriptions/sync-language-service-subscriptions', {
            payload: { serviceId: service.service_id, createInactive },
        })
            .then(
                (syncResponse) => {
                    if (currentRequestToken !== requestTokenRef.current) {
                        return
                    }
                    setResponse(syncResponse)
                    setIsSyncSuccessful(true)
                },
                (errorResponse) => {
                    if (currentRequestToken !== requestTokenRef.current) {
                        return
                    }
                    setResponse(errorResponse)
                    setIsSyncSuccessful(false)
                },
            )
            .finally(() => {
                if (currentRequestToken !== requestTokenRef.current) {
                    return
                }
                setIsLoading(false)
            })
    }

    if (!service.canonical_service_id) {
        return null
    }

    return (
        <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
            <AlertDialogTrigger asChild>
                <Button>
                    <Icon name="sync" /> Sync Language Subscriptions
                </Button>
            </AlertDialogTrigger>
            <AlertDialogPortal>
                <AlertDialogOverlay className="bg-black/50 backdrop-blur-sm fixed inset-0 z-50" />
                <AlertDialogContent className="bg-white max-w-lg">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Sync Language Service Subscriptions</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will create subscriptions for all providers that have an active
                            subscription for the canonical service (ID:{' '}
                            {service.canonical_service_id}) but do not yet have any subscription for
                            this localized service. Options will be mapped according to the
                            configured option mappings. Working areas will be copied from the
                            canonical subscription.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="flex items-center gap-2 py-2">
                        <Switch
                            id="create-inactive"
                            checked={createInactive}
                            onCheckedChange={setCreateInactive}
                        />
                        <Label htmlFor="create-inactive">Create inactive subscriptions</Label>
                    </div>
                    {response ? (
                        <pre className="rounded-md bg-neutral-50 p-3 text-sm overflow-auto max-h-48">
                            {JSON.stringify(response, null, 2)}
                        </pre>
                    ) : null}
                    <AlertDialogFooter>
                        {isSyncSuccessful ? (
                            <AlertDialogCancel>Close</AlertDialogCancel>
                        ) : (
                            <>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <Button onClick={sync}>
                                    {isLoading ? <LoadingDots color="white" /> : 'Sync'}
                                </Button>
                            </>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogPortal>
        </AlertDialog>
    )
}

export default SyncLocalizedServiceSubscriptionsButton
